import AuthClientStore from './auth-client.service';
import { SUPPORT_FRAME_ENDED_MESSAGE, supportFrameSessionId } from './support-frame';

/** Fired when this tab's support session is over (ended, ran out, or refused by the API). */
export const SUPPORT_SESSION_ENDED_EVENT = 'ardealy:support-session-ended';

/** What the API answers when a support session may not do something. */
export const SUPPORT_REFUSALS = ['SUPPORT_READ_ONLY', 'SUPPORT_NOT_ALLOWED'] as const;
export type SupportRefusal = typeof SUPPORT_REFUSALS[number];

export const isSupportRefusal = (code?: string): code is SupportRefusal =>
    !!code && (SUPPORT_REFUSALS as readonly string[]).includes(code);

/**
 * End a support session from the super admin's page while it goes away (tab
 * closed, reload, navigation). A keepalive request outlives the page; nothing
 * waits for the answer.
 */
export function endSupportSessionOnUnload(sessionId: string): void {
    const token = AuthClientStore.getSignInAccessToken();
    if (!token) return;
    try {
        void fetch(`${process.env.REACT_APP_PMS_API_URL}/support-sessions/${sessionId}/end`, {
            method: 'POST',
            keepalive: true,
            headers: { Authorization: `Bearer ${token}` },
        }).catch(() => undefined);
    } catch {
        // The session still ends on its own within the hour.
    }
}

/** Where a super admin lands after a support session: that vendor's support history. */
export const supportReturnPath = (vendorId?: string | null) =>
    vendorId ? `/vendors/${vendorId}/edit?tab=support` : '/vendors';

/**
 * Drop the frame's support token and tell the super admin's page, which closes
 * the frame. The messages carry the vendor, read before the token goes.
 */
export function endSupportSessionLocally(): void {
    const sessionId = supportFrameSessionId();
    const vendorId = AuthClientStore.supportVendorId();
    AuthClientStore.removeSupportToken();
    window.dispatchEvent(new CustomEvent(SUPPORT_SESSION_ENDED_EVENT, { detail: { vendorId } }));
    if (sessionId && window.parent !== window) {
        window.parent.postMessage({ type: SUPPORT_FRAME_ENDED_MESSAGE, sessionId, vendorId }, window.location.origin);
    }
}
